import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Chip,
  LinearProgress,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Divider,
  Fade,
  Skeleton
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Lightbulb as LightbulbIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import AIAnalysis from './AIAnalysis';
import PromptSuggestions from './PromptSuggestions';
import SecurityCodeTest from './SecurityCodeTest';
import AgentPerformanceView from './AgentPerformanceView';
import AuthStatusAnalytics from './AuthStatusAnalytics';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-hub-tabpanel-${index}`}
      aria-labelledby={`ai-hub-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AIHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      setRefreshing(true);
      // Fetch quick stats from APIs
      const [analysisRes, suggestionsRes, securityRes] = await Promise.all([
        axios.get('/api/ai-analysis/stats'),
        axios.get('/api/prompt-suggestions/suggestions?days=7'),
        axios.get('/api/security-code-test/run')
      ]).catch(() => [null, null, null]); // Graceful failure

      setStats({
        totalCalls: analysisRes?.data?.total_calls || 0,
        averageGrade: analysisRes?.data?.average_grade || 0,
        criticalIssues: suggestionsRes?.data?.calls_with_critical_issues || 0,
        suggestions: suggestionsRes?.data?.suggestions?.length || 0,
        securityCoverage: securityRes?.data?.coverage_percentage || 0,
        lastUpdate: new Date().toLocaleTimeString()
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        totalCalls: 0,
        averageGrade: 0,
        criticalIssues: 0,
        suggestions: 0,
        securityCoverage: 0,
        lastUpdate: new Date().toLocaleTimeString()
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    fetchStats();
  };

  // Beautiful stat cards with gradients
  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    gradient, 
    trend,
    onClick 
  }: any) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        background: gradient,
        color: 'white',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 6
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3, position: 'relative' }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1}>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.9, 
                mb: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.75rem'
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h3" 
              fontWeight={700}
              sx={{ mb: 0.5 }}
            >
              {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} /> : value}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ opacity: 0.85, fontSize: '0.875rem' }}
            >
              {subtitle}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                <TrendingUpIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              width: 56,
              height: 56,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Icon sx={{ fontSize: 28 }} />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          pt: 8,
          pb: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(40px)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            filter: 'blur(60px)'
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    width: 64,
                    height: 64,
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography 
                    variant="h3" 
                    fontWeight={700}
                    sx={{ 
                      mb: 0.5,
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    Fona AI Intelligence Hub
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      opacity: 0.95,
                      fontWeight: 400
                    }}
                  >
                    Performance Analysis · Prompt Optimization · Security Testing
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <Chip
                icon={<TimerIcon />}
                label={`Updated ${stats?.lastUpdate || '...'}`}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Box>
          </Box>

          {/* Stats Grid */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Calls Analyzed"
                value={stats?.totalCalls || 0}
                subtitle="Last 7 days"
                icon={PhoneIcon}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                onClick={() => setActiveTab(0)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Average AI Grade"
                value={stats?.averageGrade ? `${stats.averageGrade}/10` : '0/10'}
                subtitle="Performance score"
                icon={TrendingUpIcon}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                trend={stats?.averageGrade >= 7 ? 'Excellent' : stats?.averageGrade >= 5 ? 'Good' : 'Needs improvement'}
                onClick={() => setActiveTab(0)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Critical Issues"
                value={stats?.criticalIssues || 0}
                subtitle={`${stats?.suggestions || 0} suggestions`}
                icon={WarningIcon}
                gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                onClick={() => setActiveTab(1)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Security Coverage"
                value={stats?.securityCoverage ? `${stats.securityCoverage}%` : '0%'}
                subtitle="Customers with code"
                icon={SecurityIcon}
                gradient="linear-gradient(135deg, #30cfd0 0%, #330867 100%)"
                onClick={() => setActiveTab(2)}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: -6, position: 'relative', zIndex: 2 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper'
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="AI Hub tabs"
              sx={{
                px: 3,
                '& .MuiTab-root': {
                  minHeight: 72,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                }
              }}
            >
              <Tab
                icon={<AssessmentIcon />}
                iconPosition="start"
                label="Performance Analysis"
                id="ai-hub-tab-0"
                aria-controls="ai-hub-tabpanel-0"
              />
              <Tab
                icon={<LightbulbIcon />}
                iconPosition="start"
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Prompt Suggestions
                    {stats?.suggestions > 0 && (
                      <Chip
                        label={stats.suggestions}
                        size="small"
                        color="warning"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                }
                id="ai-hub-tab-1"
                aria-controls="ai-hub-tabpanel-1"
              />
              <Tab
                icon={<SecurityIcon />}
                iconPosition="start"
                label="Security Code Test"
                id="ai-hub-tab-2"
                aria-controls="ai-hub-tabpanel-2"
              />
              <Tab
                icon={<TrendingUpIcon />}
                iconPosition="start"
                label="Agent Performance"
                id="ai-hub-tab-3"
                aria-controls="ai-hub-tabpanel-3"
              />
              <Tab
                icon={<CheckCircleIcon />}
                iconPosition="start"
                label="Auth Status Analytics"
                id="ai-hub-tab-4"
                aria-controls="ai-hub-tabpanel-4"
              />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            <AIAnalysis />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <PromptSuggestions />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <SecurityCodeTest />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <AgentPerformanceView />
          </TabPanel>
          <TabPanel value={activeTab} index={4}>
            <AuthStatusAnalytics />
          </TabPanel>
        </Paper>
      </Container>

      {/* Footer spacing */}
      <Box sx={{ pb: 8 }} />

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default AIHub;

